/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from "recharts";
import { MotionCard } from "../motion/AdminMotion";

function formatWhen(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return "Just now";
    return d.toLocaleString([], {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Just now";
  }
}

function asLabel(log) {
  return log?.categoryLabel || log?.category || "Category";
}

function classDisplayName(item) {
  return item?.name || item?.className || item?.yearGroup || item?.label || item?.id || "Class";
}

function toPoints(value) {
  const n = Number(String(value || "0").replace(/\s/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getSchoolWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // Sunday = 0, Monday = 1
  const diffToMonday = day === 0 ? -6 : 1 - day;

  d.setDate(d.getDate() + diffToMonday);
  d.setHours(0, 0, 0, 0);

  return d;
}

function toDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildSchoolWeekDays(logs) {
  const monday = getSchoolWeekStart(new Date());
  const days = [];

  for (let i = 0; i < 5; i += 1) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);

    days.push({
      key: toDateKey(d),
      label: d.toLocaleDateString([], { weekday: "short" }),
      positive: 0,
      negative: 0,
      total: 0,
    });
  }

  logs.forEach((log) => {
    const dt = log?.createdAt?.toDate ? log.createdAt.toDate() : null;
    if (!dt) return;

    const day = days.find((x) => x.key === toDateKey(dt));
    if (!day) return;

    day.total += 1;
    if (String(log.type || "").toLowerCase() === "negative") day.negative += 1;
    else day.positive += 1;
  });

  return days;
}

function buildStudentBuckets(logs) {
  const map = {};

  logs.forEach((log) => {
    const key = log.studentId || log.studentName || "unknown";
    if (!map[key]) {
      map[key] = {
        id: key,
        studentId: log.studentId || "",
        studentName: log.studentName || "Student",
        className: log.className || "Class",
        score: 0,
        positive: 0,
        negative: 0,
        total: 0,
        parentAlerts: 0,
      };
    }

    map[key].score += toPoints(log.points);
    map[key].total += 1;
    if (log.notifyParent) map[key].parentAlerts += 1;
    if (log.type === "negative") map[key].negative += 1;
    else map[key].positive += 1;
  });

  return Object.values(map).sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.positive !== a.positive) return b.positive - a.positive;
    return String(a.studentName).localeCompare(String(b.studentName));
  });
}

function buildClassBuckets(logs) {
  const map = {};

  logs.forEach((log) => {
    const key = log.classId || log.className || "unknown";
    if (!map[key]) {
      map[key] = {
        id: key,
        className: log.className || "Unknown Class",
        total: 0,
        positive: 0,
        negative: 0,
        score: 0,
      };
    }

    map[key].total += 1;
    map[key].score += toPoints(log.points);
    if (log.type === "negative") map[key].negative += 1;
    else map[key].positive += 1;
  });

  return Object.values(map).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return String(a.className).localeCompare(String(b.className));
  });
}

function buildCategoryBuckets(logs, type) {
  const map = {};

  logs
    .filter((x) => x.type === type)
    .forEach((log) => {
      const key = asLabel(log);
      if (!map[key]) map[key] = { label: key, count: 0, score: 0 };
      map[key].count += 1;
      map[key].score += toPoints(log.points);
    });

  return Object.values(map).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return String(a.label).localeCompare(String(b.label));
  });
}

function toneForType(type) {
  return String(type || "").toLowerCase() === "negative"
    ? { bg: "#fff1f2", color: "#be123c", border: "#fecdd3", accent: "#ef4444" }
    : { bg: "#ecfdf3", color: "#166534", border: "#bbf7d0", accent: "#22c55e" };
}

function getRisk(student) {
  if (!student) return { label: "Stable", color: "#027a48", bg: "#dcfae6" };
  if (student.negative >= 3 || student.score < 0) {
    return { label: "Concern", color: "#b42318", bg: "#fee4e2" };
  }
  if (student.negative >= 1) {
    return { label: "Watch", color: "#b54708", bg: "#fef0c7" };
  }
  return { label: "Stable", color: "#027a48", bg: "#dcfae6" };
}

export default function AdminBehaviourDashboard({
  schoolId,
  classes = [],
  schoolName = "Your School",
  brandPrimary = "#4f46e5",
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    if (!schoolId) {
      setLogs([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");

    const weekStart = getSchoolWeekStart(new Date());

    const q = query(
      collection(db, "schools", schoolId, "behaviourLogs"),
      where("createdAt", ">=", Timestamp.fromDate(weekStart)),
      orderBy("createdAt", "desc"),
      limit(400)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Admin behaviour dashboard load failed:", err);
        setError("Could not load behaviour analytics.");
        setLogs([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [schoolId]);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((log) => {
      if (selectedClassId !== "all" && log.classId !== selectedClassId) return false;
      if (selectedType !== "all" && log.type !== selectedType) return false;
      if (!q) return true;

      return [log.studentName, log.className, log.teacherName, log.categoryLabel, log.category, log.points]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [logs, selectedClassId, selectedType, search]);

  const summary = useMemo(() => {
    const positive = filteredLogs.filter((x) => x.type === "positive").length;
    const negative = filteredLogs.filter((x) => x.type === "negative").length;
    return {
      total: filteredLogs.length,
      positive,
      negative,
      notifyParent: filteredLogs.filter((x) => !!x.notifyParent).length,
      positiveRatio: pct(positive, filteredLogs.length),
      negativeRatio: pct(negative, filteredLogs.length),
    };
  }, [filteredLogs]);

  const schoolWeekDays = useMemo(() => buildSchoolWeekDays(filteredLogs), [filteredLogs]);
  const studentBuckets = useMemo(() => buildStudentBuckets(filteredLogs), [filteredLogs]);
  const classBuckets = useMemo(() => buildClassBuckets(filteredLogs), [filteredLogs]);
  const positiveCategories = useMemo(
    () => buildCategoryBuckets(filteredLogs, "positive"),
    [filteredLogs]
  );
  const negativeCategories = useMemo(
    () => buildCategoryBuckets(filteredLogs, "negative"),
    [filteredLogs]
  );

  const topStudent = studentBuckets[0];

  const selectedStudentLogs = useMemo(() => {
    if (!selectedStudent) return [];
    return logs
      .filter((log) => {
        const key = log.studentId || log.studentName || "unknown";
        return key === selectedStudent.id || log.studentName === selectedStudent.studentName;
      })
      .sort((a, b) => {
        const at = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bt = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bt - at;
      });
  }, [logs, selectedStudent]);

  return (
    <div style={styles.wrap}>
      <MotionCard style={styles.hero} hover={false}>
        <div>
          <div style={{ ...styles.heroEyebrow, color: brandPrimary }}>
            {schoolName} admin
          </div>
          <h2 style={styles.heroTitle}>Behaviour Intelligence</h2>
          <p style={styles.heroText}>
            Premium analytics for behaviour trends, praise tracking, concern monitoring,
            student scoring, and behaviour insights.
          </p>
        </div>

        <div
          style={{
            ...styles.heroMetric,
            background: `linear-gradient(135deg, ${brandPrimary} 0%, #6d5cff 100%)`,
          }}
        >
          <span>Live behaviour logs</span>
          <strong>{summary.total}</strong>
          <small>
            {summary.positive} positive • {summary.negative} negative
          </small>
        </div>
      </MotionCard>

      <MotionCard style={styles.filtersCard} hover={false}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search student, class, teacher, category..."
          style={styles.input}
        />

        <select
          value={selectedClassId}
          onChange={(e) => setSelectedClassId(e.target.value)}
          style={styles.select}
        >
          <option value="all">All Classes</option>
          {classes.map((item) => (
            <option key={item.id} value={item.id}>
              {classDisplayName(item)}
            </option>
          ))}
        </select>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={styles.select}
        >
          <option value="all">All Types</option>
          <option value="positive">Positive</option>
          <option value="negative">Negative</option>
        </select>
      </MotionCard>

      {error ? <div style={styles.errorBox}>{error}</div> : null}

      <section style={styles.kpiGrid}>
        <StatCard label="Total Logs" value={loading ? "—" : summary.total} />
        <StatCard label="Positive" value={loading ? "—" : summary.positive} color="#16a34a" />
        <StatCard label="Negative" value={loading ? "—" : summary.negative} color="#dc2626" />
        <StatCard
          label="Top Student"
          value={loading ? "—" : topStudent?.studentName || "—"}
          color="#4f46e5"
        />
      </section>

      <section style={styles.analyticsGrid}>
        <MotionCard style={styles.chartPanel} hover={false}>
          <div style={styles.panelTop}>
            <div>
              <div style={styles.eyebrow}>Behaviour Trend</div>
              <h3 style={styles.panelTitle}>This School Week</h3>
            </div>
            <span style={styles.softBadge}>Mon–Fri</span>
          </div>

          <div style={styles.chartWrap}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={schoolWeekDays}>
                <defs>
                  <linearGradient id="positiveFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="negativeFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8eefb" />
                <XAxis dataKey="label" tick={{ fill: "#64748b", fontWeight: 800 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontWeight: 800 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="positive" stroke="#22c55e" fill="url(#positiveFill)" strokeWidth={3} />
                <Area type="monotone" dataKey="negative" stroke="#ef4444" fill="url(#negativeFill)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </MotionCard>

        <MotionCard style={styles.chartPanel} hover={false}>
          <div style={styles.panelTop}>
            <div>
              <div style={styles.eyebrow}>Category Intelligence</div>
              <h3 style={styles.panelTitle}>Praise & Concern Breakdown</h3>
            </div>
          </div>

          <div style={styles.twoChartGrid}>
            <CategoryChart
              title="Praise Breakdown"
              data={positiveCategories.slice(0, 5)}
              color="#635bff"
              emptyText="No positive categories yet."
            />
            <CategoryChart
              title="Concern Breakdown"
              data={negativeCategories.slice(0, 5)}
              color="#ef4444"
              emptyText="No concern categories yet."
            />
          </div>
        </MotionCard>
      </section>

      <section style={styles.mainGrid}>
        <MotionCard style={styles.panel} hover={false}>
          <div style={styles.panelTop}>
            <div>
              <div style={styles.eyebrow}>Student Rankings</div>
              <h3 style={styles.panelTitle}>Top Student Scores</h3>
            </div>
          </div>

          <div style={styles.rankList}>
            {studentBuckets.length ? (
              studentBuckets.slice(0, 8).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  style={styles.rankCardButton}
                  onClick={() => setSelectedStudent(item)}
                >
                  <div>
                    <div style={styles.rankTitle}>{item.studentName}</div>
                    <div style={styles.rankSub}>
                      {item.className} • {item.positive} positive • {item.negative} negative
                    </div>
                  </div>
                  <strong
                    style={{
                      ...styles.rankValue,
                      color: item.score >= 0 ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {item.score > 0 ? `+${item.score}` : item.score}
                  </strong>
                </button>
              ))
            ) : (
              <Empty text="No student score data yet." />
            )}
          </div>
        </MotionCard>

        <MotionCard style={styles.panel} hover={false}>
          <div style={styles.panelTop}>
            <div>
              <div style={styles.eyebrow}>Class Hotspots</div>
              <h3 style={styles.panelTitle}>Behaviour By Class</h3>
            </div>
          </div>

          <div style={styles.rankList}>
            {classBuckets.length ? (
              classBuckets.slice(0, 8).map((item) => (
                <div key={item.id} style={styles.rankCard}>
                  <div>
                    <div style={styles.rankTitle}>{item.className}</div>
                    <div style={styles.rankSub}>
                      {item.positive} positive • {item.negative} negative
                    </div>
                  </div>
                  <strong style={styles.rankValue}>{item.total}</strong>
                </div>
              ))
            ) : (
              <Empty text="No class behaviour data yet." />
            )}
          </div>
        </MotionCard>
      </section>

      {selectedStudent ? (
        <StudentDrawer
          student={selectedStudent}
          logs={selectedStudentLogs}
          onClose={() => setSelectedStudent(null)}
        />
      ) : null}
    </div>
  );
}

function CategoryChart({ title, data, color, emptyText }) {
  return (
    <div style={styles.categoryChartCell}>
      <h4 style={styles.smallChartTitle}>{title}</h4>
      {data.length ? (
        <div style={styles.smallChartWrap}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eefb" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#64748b", fontWeight: 800, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis tick={{ fill: "#64748b", fontWeight: 800 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={color} radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty text={emptyText} />
      )}
    </div>
  );
}

function StatCard({ label, value, color = "#111827" }) {
  return (
    <MotionCard style={styles.statCard} hover={false}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color }}>{value}</div>
    </MotionCard>
  );
}

function Empty({ text }) {
  return <div style={styles.emptyCard}>{text}</div>;
}

function StudentDrawer({ student, logs, onClose }) {
  const risk = getRisk(student);
  const ratioPositive = pct(student.positive, student.total);
  const ratioNegative = pct(student.negative, student.total);

  return (
    <div style={styles.drawerOverlay} onClick={onClose}>
      <div style={styles.drawerCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.drawerTop}>
          <div>
            <div style={styles.drawerEyebrow}>Student Behaviour Profile</div>
            <h2 style={styles.drawerTitle}>{student.studentName}</h2>
            <p style={styles.drawerSub}>{student.className}</p>
          </div>

          <button type="button" style={styles.drawerClose} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={{ ...styles.riskBadge, color: risk.color, background: risk.bg }}>
          {risk.label}
        </div>

        <div style={styles.drawerStats}>
          <MiniStat label="Points" value={student.score > 0 ? `+${student.score}` : student.score} />
          <MiniStat label="Positive" value={student.positive} color="#16a34a" />
          <MiniStat label="Negative" value={student.negative} color="#dc2626" />
          <MiniStat label="Alerts" value={student.parentAlerts} color="#2563eb" />
        </div>

        <div style={styles.drawerPanel}>
          <div style={styles.drawerPanelTitle}>Positive vs negative</div>
          <div style={styles.ratioTrack}>
            <div style={{ ...styles.ratioPositive, width: `${ratioPositive}%` }} />
            <div style={{ ...styles.ratioNegative, width: `${ratioNegative}%` }} />
          </div>
          <div style={styles.ratioMeta}>
            <span>{ratioPositive}% positive</span>
            <span>{ratioNegative}% negative</span>
          </div>
        </div>

        <div style={styles.drawerPanel}>
          <div style={styles.drawerPanelTitle}>Recent behaviour timeline</div>
          <div style={styles.timeline}>
            {logs.length ? (
              logs.slice(0, 12).map((log) => {
                const tone = toneForType(log.type);
                return (
                  <div key={log.id} style={styles.timelineItem}>
                    <div style={{ ...styles.timelineDot, background: tone.accent }} />
                    <div style={styles.timelineContent}>
                      <div style={styles.timelineTop}>
                        <strong>{asLabel(log)}</strong>
                        <span style={{ color: tone.color }}>
                          {log.type === "negative" ? "Negative" : "Positive"}
                        </span>
                      </div>

                      {log.note ? <p style={styles.timelineNote}>{log.note}</p> : null}

                      <div style={styles.timelineMeta}>
                        <span>{log.teacherName || "Teacher"}</span>
                        <span>{formatWhen(log.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <Empty text="No behaviour timeline yet." />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color = "#112042" }) {
  return (
    <div style={styles.miniStat}>
      <div style={styles.miniLabel}>{label}</div>
      <div style={{ ...styles.miniValue, color }}>{value}</div>
    </div>
  );
}

const styles = {
  wrap: {
    display: "grid",
    gap: 16,
    paddingBottom: 24,
  },

  hero: {
    borderRadius: 30,
    background: "linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)",
    border: "1px solid #dbe7ff",
    padding: 24,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 340px",
    gap: 18,
    alignItems: "stretch",
    boxShadow: "0 18px 45px rgba(15,23,42,0.05)",
  },

  heroEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 950,
    marginBottom: 10,
  },

  heroTitle: {
    margin: 0,
    fontSize: 44,
    lineHeight: 1,
    letterSpacing: "-0.055em",
    color: "#112042",
    fontWeight: 950,
  },

  heroText: {
    marginTop: 14,
    marginBottom: 0,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 700,
  },

  heroMetric: {
    borderRadius: 26,
    padding: 24,
    color: "#fff",
    display: "grid",
    alignContent: "center",
    minHeight: 150,
    boxShadow: "0 22px 44px rgba(79,70,229,0.18)",
  },

  filtersCard: {
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 16,
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 220px 180px",
    gap: 12,
    boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
  },

  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#fff",
    outline: "none",
    fontSize: 14,
    color: "#111827",
    boxSizing: "border-box",
  },

  select: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#fff",
    outline: "none",
    fontSize: 14,
    color: "#111827",
    boxSizing: "border-box",
  },

  errorBox: {
    borderRadius: 18,
    padding: 16,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontWeight: 850,
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },

  statCard: {
    borderRadius: 22,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 18,
    minHeight: 105,
    boxShadow: "0 12px 30px rgba(15,23,42,0.04)",
    display: "grid",
    alignContent: "space-between",
  },

  statLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    fontWeight: 950,
    color: "#64748b",
  },

  statValue: {
    marginTop: 16,
    fontSize: 32,
    fontWeight: 950,
    lineHeight: 1.1,
  },

  analyticsGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: 16,
    alignItems: "stretch",
  },

  chartPanel: {
    borderRadius: 26,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 20,
    boxShadow: "0 14px 34px rgba(15,23,42,0.05)",
    minHeight: 330,
    minWidth: 0,
  },

  chartWrap: {
    width: "100%",
    height: 240,
    minHeight: 240,
    minWidth: 0,
  },

  twoChartGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    alignItems: "stretch",
  },

  categoryChartCell: {
    minWidth: 0,
  },

  smallChartTitle: {
    margin: "0 0 10px",
    color: "#112042",
    fontSize: 16,
    fontWeight: 950,
  },

  smallChartWrap: {
    width: "100%",
    height: 220,
    minHeight: 220,
    minWidth: 0,
  },

  panelTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 14,
  },

  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    fontWeight: 950,
    color: "#64748b",
    marginBottom: 8,
  },

  panelTitle: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.05,
    color: "#112042",
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },

  softBadge: {
    borderRadius: 999,
    padding: "8px 12px",
    background: "#eef4ff",
    color: "#2845d9",
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
    gap: 16,
    alignItems: "stretch",
  },

  panel: {
    borderRadius: 26,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 20,
    boxShadow: "0 14px 34px rgba(15,23,42,0.05)",
    minWidth: 0,
  },

  rankList: {
    display: "grid",
    gap: 12,
    maxHeight: 430,
    overflowY: "auto",
    paddingRight: 6,
  },

  rankCard: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
    padding: 14,
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },

  rankCardButton: {
    width: "100%",
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
    padding: 14,
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    textAlign: "left",
    cursor: "pointer",
  },

  rankTitle: {
    fontSize: 15,
    fontWeight: 950,
    color: "#112042",
  },

  rankSub: {
    marginTop: 4,
    fontSize: 12,
    color: "#64748b",
    fontWeight: 750,
  },

  rankValue: {
    fontSize: 24,
    lineHeight: 1,
    fontWeight: 950,
    color: "#112042",
    whiteSpace: "nowrap",
  },

  emptyCard: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    padding: 16,
    color: "#64748b",
    fontWeight: 800,
  },

  drawerOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    background: "rgba(15,23,42,0.42)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    display: "flex",
    justifyContent: "flex-end",
  },

  drawerCard: {
    width: "min(520px, 100%)",
    height: "100vh",
    background: "linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)",
    borderLeft: "1px solid rgba(219,231,255,0.95)",
    boxShadow: "-30px 0 80px rgba(15,23,42,0.22)",
    padding: 26,
    overflowY: "auto",
  },

  drawerTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: 16,
  },

  drawerEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 950,
    color: "#64748b",
    marginBottom: 8,
  },

  drawerTitle: {
    margin: 0,
    fontSize: 34,
    lineHeight: 1,
    letterSpacing: "-0.05em",
    color: "#112042",
    fontWeight: 950,
  },

  drawerSub: {
    margin: "8px 0 0",
    color: "#64748b",
    fontWeight: 800,
  },

  drawerClose: {
    width: 48,
    height: 48,
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    color: "#112042",
    fontSize: 26,
    fontWeight: 950,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    lineHeight: 1,
    padding: 0,
  },

  riskBadge: {
    display: "inline-flex",
    padding: "10px 14px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 16,
  },

  drawerStats: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 10,
    marginBottom: 16,
  },

  miniStat: {
    borderRadius: 18,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 12,
  },

  miniLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontWeight: 950,
    color: "#64748b",
    marginBottom: 8,
  },

  miniValue: {
    fontSize: 22,
    lineHeight: 1,
    fontWeight: 950,
  },

  drawerPanel: {
    borderRadius: 22,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 16,
    marginBottom: 14,
  },

  drawerPanelTitle: {
    fontSize: 15,
    fontWeight: 950,
    color: "#112042",
    marginBottom: 12,
  },

  ratioTrack: {
    height: 14,
    borderRadius: 999,
    background: "#eef2ff",
    overflow: "hidden",
    display: "flex",
  },

  ratioPositive: {
    height: "100%",
    background: "#22c55e",
  },

  ratioNegative: {
    height: "100%",
    background: "#ef4444",
  },

  ratioMeta: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#64748b",
    fontWeight: 850,
  },

  timeline: {
    display: "grid",
    gap: 12,
  },

  timelineItem: {
    display: "grid",
    gridTemplateColumns: "12px minmax(0,1fr)",
    gap: 10,
  },

  timelineDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    marginTop: 7,
  },

  timelineContent: {
    borderRadius: 18,
    background: "#fbfdff",
    border: "1px solid #dbe7ff",
    padding: 12,
  },

  timelineTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: "#112042",
    fontSize: 13,
    fontWeight: 900,
  },

  timelineNote: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
  },

  timelineMeta: {
    marginTop: 10,
    paddingTop: 8,
    borderTop: "1px solid #edf2ff",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
};
